import React, { useState } from "react";
import BottomClipModal from "./components/Modal/BottomClipModal";
import { mdi_light_camera, bytesize_microphone, weui_location } from "assets";
import styled from "styled-components";
import { Footer, ProceedButton } from "./components";
import { 
  requestCameraPermission, 
  requestMicrophonePermission, 
  requestLocationPermission,
  getPermissionErrorMessage
} from "../../utils/permissions";

const PermissionModalContainer = styled.div`

`;

const PermissionModalContentContainer = styled.div`
  padding: 15px 20px;
`;

const PermissionsList = styled.div`
  margin: 15px 0;
`;

const PermissionsCard = styled.div`
  display: flex;
  align-items: flex-start;
  margin: 18px 0;
`;

const PermissionsIcon = styled.img`
  width: 42px;
  margin-right: 10px;
  margin-top: 4px;
`;

const Title = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: rgb(31, 28, 34);
  margin-bottom: 20px;
  padding: 10px;
`;

const PermissionsTextContainer = styled.div`
  flex: 1;
`;

const PermissionsTitle = styled.h3`
  font-weight: 600;
  font-size: 18px;
  margin: 0;
  color: #1f1c22;
`;

const PermissionsSubtitle = styled.p`
  font-size: 16px;
  color: #666;
  margin: 4px 0 0;
`;

const TickIcon = styled.span`
  font-size: 18px;
  color: green;
  margin-left: 10px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 30px;
`;

const SkipButton = styled.button`
  background: none;
  border: none;
  color: #666;
  font-size: 16px;
  cursor: pointer;
`;

const ProceedButtonStyled = styled(ProceedButton)`
  padding: 10px 50px;
  width:50%;
`;

const permissions = [
  {
    icon: mdi_light_camera,
    title: "Camera",
    subtitle: "Needed for identity verification.",
    key: "camera",
  },
  {
    icon: bytesize_microphone,
    title: "Microphone",
    subtitle: "For voice authentication.",
    key: "microphone",
  },
  {
    icon: weui_location,
    title: "Location",
    subtitle: "Helps enhance security and fraud prevention.",
    key: "location",
  },
];

interface GrantedPermissions {
  camera?: boolean;
  microphone?: boolean;
  location?: boolean;
}

const PermissionErrorText = styled.p`
  color: #ff6b6b;
  font-size: 14px;
  margin: 4px 0;
  text-align: left;
`;

const GrantPermissions = ({
  isOpen,
  onClose,
  onPermission,
  onDeniedPermission,
  setCameraAndMicPermission,
  setLocationPermission,
}) => {
  const [grantedPermissions, setGrantedPermissions] = useState<GrantedPermissions>({});
  const [currentPermissionIndex, setCurrentPermissionIndex] = useState(0);
  const [permissionInProgress, setPermissionInProgress] = useState(false);
  const [permissionErrors, setPermissionErrors] = useState<{[key: string]: string}>({});

  const requestNextPermission = async () => {
    if (currentPermissionIndex >= permissions.length) {
      // All permissions processed
      if (grantedPermissions.camera && grantedPermissions.microphone) {
        setCameraAndMicPermission(true);
        if (grantedPermissions.location) {
          setLocationPermission(true);
          onPermission();
        } else {
          onDeniedPermission();
        }
      } else {
        onDeniedPermission();
      }
      return;
    }

    setPermissionInProgress(true);
    setPermissionErrors({});
    
    // Request camera and microphone first
    if (currentPermissionIndex === 0) {
      await requestCameraAndMicrophonePermissions();
    } else if (currentPermissionIndex === 2) { // Skip index 1 since we handle both camera and mic at once
      await requestLocationPermissionHandler();
    }
  };

  const requestCameraAndMicrophonePermissions = async () => {
    try {
      // Request camera permission
      await requestCameraPermission();
      
      // If camera succeeded, request microphone
      await requestMicrophonePermission();
      
      // If both succeeded, update state
      setGrantedPermissions(prev => ({
        ...prev,
        camera: true,
        microphone: true
      }));
      
      // Skip the microphone permission since we already got it
      setCurrentPermissionIndex(2);
      setPermissionInProgress(false);
    } catch (error) {
      console.error("Permission error:", error);
      // Update permissions state based on which one failed
      setGrantedPermissions(prev => ({
        ...prev,
        camera: error.errorType !== 'camera',
        microphone: error.errorType !== 'microphone'
      }));
      
      // Set error message
      setPermissionErrors({
        ...permissionErrors,
        [error.errorType]: getPermissionErrorMessage(error)
      });
      
      setCurrentPermissionIndex(2); // Still move on to location
      setPermissionInProgress(false);
    }
  };

  const requestLocationPermissionHandler = async () => {
    try {
      await requestLocationPermission();
      setGrantedPermissions(prev => ({
        ...prev,
        location: true
      }));
      setCurrentPermissionIndex(prev => prev + 1);
      setPermissionInProgress(false);
    } catch (error) {
      console.error("Location permission error:", error);
      setGrantedPermissions(prev => ({
        ...prev,
        location: false
      }));
      setPermissionErrors({
        ...permissionErrors,
        location: getPermissionErrorMessage(error)
      });
      setCurrentPermissionIndex(prev => prev + 1);
      setPermissionInProgress(false);
    }
  };

  const handlePermissions = () => {
    setCurrentPermissionIndex(0);
    setPermissionErrors({});
    setGrantedPermissions({});
    requestNextPermission();
  };

  // When permission status changes, check if we need to request the next permission
  React.useEffect(() => {
    if (!permissionInProgress && currentPermissionIndex < permissions.length) {
      requestNextPermission();
    }
  }, [currentPermissionIndex, permissionInProgress]);

  return (
    <BottomClipModal isOpen={isOpen} onClose={onClose}>
      <PermissionModalContainer>
        <PermissionModalContentContainer>
          <Title>Please Grant the permission below</Title>
          <PermissionsList>
            {permissions.map((permission, index) => (
              <PermissionsCard key={index}>
                <PermissionsIcon
                  src={permission.icon}
                  alt={`${permission.title} icon`}
                />
                <PermissionsTextContainer>
                  <PermissionsTitle>{permission.title}</PermissionsTitle>
                  <PermissionsSubtitle>
                    {permission.subtitle}
                  </PermissionsSubtitle>
                  {permissionErrors[permission.key] && (
                    <PermissionErrorText>
                      {permissionErrors[permission.key]}
                    </PermissionErrorText>
                  )}
                </PermissionsTextContainer>
                {grantedPermissions[permission.key] && <TickIcon>✔️</TickIcon>}
              </PermissionsCard>
            ))}
          </PermissionsList>
          <ButtonContainer>
            <ProceedButtonStyled 
              onClick={handlePermissions}
              disabled={permissionInProgress}
            >
              {permissionInProgress ? "Processing..." : "Next"}
            </ProceedButtonStyled>
          </ButtonContainer>
        </PermissionModalContentContainer>
      </PermissionModalContainer>
    </BottomClipModal>
  );
};

export default GrantPermissions;
